import { FormAction } from '@anytag/shared-types';
import { useQueryHelper, useToggle } from '@anytag/shared-hooks';
import { cn, formatNumber, formatPrice } from '@anytag/shared-utils';
import { CURRENCY_SYMBOLS, UNEXPECTED_ERROR } from '@anytag/shared-constants';
import { BaseModal, Button, Form, ModalBody, ModalFooter } from '@anytag/shared-atoms';
import {
  namedOperations,
  TiktokAdAccountStatus,
  useTransferMoneyToAdAccountMutation,
  useWithdrawMoneyFromAdAccountMutation
} from '@anytag/graphql';
import { MoneyField } from './MoneyField';
import { TransferModalFormValues, TransferModalProps } from './types';
import { maskitoParseNumber, getTransferModalSchema } from './schema';

export const TransferModal = ({ className, adAccount, prepaidBalance }: TransferModalProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { on, setOn, setOff } = useToggle();
  const { callTransferMoneyToAdAccount, loading } = useTransferMoneyToAdAccountMutation({
    refetchQueries: [namedOperations.Query.TiktokBusinessAdAccounts]
  });
  const { callWithdrawMoneyFromAdAccount, loading: withdrawLoading } = useWithdrawMoneyFromAdAccountMutation({
    refetchQueries: [namedOperations.Query.TiktokBusinessAdAccounts]
  });

  const handleSubmit: FormAction<TransferModalFormValues>['onSubmit'] = async ({ amount, step }) => {
    try {
      if (step === 'charge') {
        await callTransferMoneyToAdAccount({
          variables: {
            input: {
              adAccountId: adAccount.id,
              amount: maskitoParseNumber(amount)
            }
          }
        });

        enqueueSnackbar(t('You have successfully transferred', { amount: `${amount} ${adAccount.currencyId}` }), {
          variant: 'success'
        });
      } else {
        await callWithdrawMoneyFromAdAccount({
          variables: {
            input: {
              adAccountId: adAccount.id,
              amount: maskitoParseNumber(amount)
            }
          }
        });

        enqueueSnackbar(t('You have successfully withdrawn', { amount: `${amount} ${adAccount.currencyId}` }), {
          variant: 'success'
        });
      }

      setOff();
    } catch (error) {
      enqueueSnackbar(t(error.message || UNEXPECTED_ERROR), { variant: 'error' });
    }
  };

  return (
    <div className={className}>
      <Button
        variant="solid"
        title={t('Button.Transfer')}
        className="min-w-26"
        onClick={setOn}
        disabled={adAccount.status !== TiktokAdAccountStatus.VERIFIED}
      />
      <BaseModal open={on} onClose={setOff} title={t('Dialog.Transfer money')} asChild>
        <Form.Provider<TransferModalFormValues>
          onSubmit={handleSubmit}
          defaultValues={{ amount: '', step: 'charge' }}
          zodSchema={getTransferModalSchema(prepaidBalance, adAccount.balanceAmount)}
        >
          {({ values: { step, amount }, formState }) => {
            const currencyPrefix = CURRENCY_SYMBOLS[adAccount.currencyId];
            const unmaskedAmount = maskitoParseNumber(amount);

            return (
              <>
                <ModalBody>
                  {/* we should have only charge step due to some TT limitations, but let's keep withdraw for now */}
                  <MoneyField
                    hasMaxValue
                    name="amount"
                    prefix={currencyPrefix}
                    triggerFormRevalidation
                    placeholder={formatPrice(10000, null, 0)}
                    balance={step === 'charge' ? prepaidBalance : adAccount.balanceAmount}
                  />

                  <div className={cn(styles.account, 'mt-6')}>
                    {step === 'withdraw' ? (
                      <>
                        <div className="font-semibold">{t('New Ad Account Balance')}</div>
                        <span className="font-semibold">
                          {amount ? `${currencyPrefix}${formatNumber(prepaidBalance || 0 + unmaskedAmount, 0)}` : '-'}
                        </span>
                      </>
                    ) : (
                      <>
                        <div className="font-semibold">{t('New Prepaid Balance')}</div>
                        <span className="font-semibold">
                          {amount ? `${currencyPrefix}${formatNumber(prepaidBalance || 0 + unmaskedAmount, 0)}` : '-'}
                        </span>
                      </>
                    )}
                  </div>

                  <div className="bg-light-gray px-3 pt-2 pb-1">
                    <div className={styles.account}>
                      <div className="text-black-60">{t('Ad Account Name')}</div>
                      <span>{adAccount.name}</span>
                    </div>
                    <div className={styles.account}>
                      <div className="text-black-50">{t('Ad Account ID')}</div>
                      <span>{adAccount.id}</span>
                    </div>
                  </div>
                </ModalBody>
                <ModalFooter
                  cancelButton={{ onClick: setOff }}
                  submitButton={{
                    type: 'submit',
                    disabled: !amount,
                    title: t(step === 'charge' ? 'Button.Charge' : 'Button.Withdraw'),
                    loading: formState.isSubmitting || loading || withdrawLoading
                  }}
                />
              </>
            );
          }}
        </Form.Provider>
      </BaseModal>
    </div>
  );
};

const styles = {
  account: cn('mb-2 flex items-center justify-between gap-2 text-base')
};
